import React, { useCallback, useMemo, useState } from 'react';
import { PlayCircle } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Col, UncontrolledPopover } from 'reactstrap';
import { Person } from 'types';
import { useIsCalibrationViewAdmin } from 'utils/models/User';
import { comparePeople } from 'utils/util/util';
import { useAdvancedDatasetFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import Avatar from 'views/Widgets/People/Avatar';
import AvatarGroup from 'views/Widgets/People/AvatarGroup';
import FilterablePeopleTable, {
  FilterType,
  SuggestionGeneratorFunction,
} from 'views/Widgets/People/FilterablePeopleTable';
import { CALIBRATION_STATUS_META } from '../constants';
import type { CalibrationViewStatus, CalibrationViewSummary } from '../types';
import { isOwnerOrAdmin } from '../utils/calibration';
import { buildIconHeader } from '../utils/table';

type ViewListRow = {
  id: number;
  name: string;
  status: CalibrationViewStatus;
  dueDateLabel: string | null;
  ownerPerson?: Person | null;
  administrators: Person[];
  reviewers: Person[];
  reviewerCount: number;
};

type CalibrationViewListProps = {
  calibrationViews: CalibrationViewSummary[];
  onSelectView: (viewId: number) => void;
  currentPerson?: Person;
  onDeleteView: (
    viewId: number,
    onSuccess: () => void,
    onError: (error: string) => void
  ) => void;
  onCreateView?: () => void;
  onViewsUpdated: (views: CalibrationViewSummary[]) => void;
  isHRBP: boolean;
};

const NAME_SORT = ['name'];
const ARRAY_PERSON_FIELDS = new Set(['administrators', 'reviewers']);

export const suggestionGenerator: SuggestionGeneratorFunction = ({
  rows,
  columnsDict,
  valuesByColumnField,
  getItemFromField,
}) => {
  const seenByField: Record<string, Set<number>> = {};
  const suggestionLists = Object.keys(valuesByColumnField).reduce(
    (acc, key) => {
      acc[key] = [];
      seenByField[key] = new Set();
      return acc;
    },
    {} as Record<string, any[]>
  );

  for (const row of rows) {
    for (const key in suggestionLists) {
      const value = row[key];
      if (!value) continue;

      if (ARRAY_PERSON_FIELDS.has(key)) {
        for (const person of value as Person[]) {
          if (!seenByField[key].has(person.id)) {
            seenByField[key].add(person.id);
            suggestionLists[key].push(person);
          }
        }
      } else if (columnsDict[key]?.isPerson) {
        if (!seenByField[key].has(value.id)) {
          seenByField[key].add(value.id);
          suggestionLists[key].push(value);
        }
      } else {
        if (!suggestionLists[key].includes(value)) {
          suggestionLists[key].push(value);
        }
      }
    }
  }

  let suggestions: any[] = [];
  for (const key in suggestionLists) {
    suggestions = suggestions.concat(
      suggestionLists[key].map((s) => getItemFromField(key, s))
    );
  }
  return suggestions;
};

const CalibrationViewList: React.FC<CalibrationViewListProps> = ({
  calibrationViews,
  onSelectView,
  onCreateView,
  onDeleteView,
  onViewsUpdated,
  currentPerson,
  isHRBP,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const { filterToText } = useAdvancedDatasetFilter(true);

  const [selectedViewIds, setSelectedViewIds] = useState<Set<number>>(
    new Set()
  );

  // a filter change can hide selected rows, so start over rather than act on views the user can't see
  const clearSelectedViews = useCallback(() => {
    setSelectedViewIds(new Set());
  }, []);

  const [deleteViewId, setDeleteViewId] = useState<number | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null
  );

  const handleDeleteClick = useCallback((viewId: number) => {
    setDeleteErrorMessage(null);
    setDeleteViewId(viewId);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteViewId(null);
    setDeleteErrorMessage(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteViewId !== null) {
      onDeleteView(
        deleteViewId,
        () => {
          setSelectedViewIds((prev) => {
            const next = new Set(prev);
            next.delete(deleteViewId);
            return next;
          });
          setDeleteViewId(null);
        },
        (error) => {
          setDeleteErrorMessage(error);
        }
      );
    }
  }, [deleteViewId, onDeleteView]);

  const isCalibrationViewAdmin = useIsCalibrationViewAdmin();
  const editableViewIds = useMemo(
    () =>
      new Set(
        calibrationViews
          .filter(
            (view) =>
              isCalibrationViewAdmin || isOwnerOrAdmin(currentPerson, view)
          )
          .map((view) => view.id)
      ),
    [calibrationViews, isCalibrationViewAdmin, currentPerson]
  );

  const isRowSelectable = useCallback(
    (row: ViewListRow) => editableViewIds.has(row.id),
    [editableViewIds]
  );

  const toggleSelectedView = useCallback((viewId: number) => {
    setSelectedViewIds((prev) => {
      const next = new Set(prev);
      if (next.has(viewId)) {
        next.delete(viewId);
      } else {
        next.add(viewId);
      }
      return next;
    });
  }, []);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const toggleStatusModal = useCallback(() => {
    setIsStatusModalOpen((isOpen) => !isOpen);
  }, []);

  const bulkStatusObject = useMemo(
    () => ({ ids: Array.from(selectedViewIds) }),
    [selectedViewIds]
  );

  const bulkStatusInputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.DROPDOWN,
        name: 'status',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.status_modal.status',
          defaultMessage: 'Status',
        }),
        // no default: a bulk change shouldn't silently fall back to Draft
        objects: Object.entries(CALIBRATION_STATUS_META(formatMessage)).map(
          ([key, meta]) => ({
            id: key,
            name: meta.name,
            icon: meta.Icon,
            iconClassName: meta.iconClassName,
          })
        ),
        renderItem: ({ icon: Icon, iconClassName, name }) => (
          <span className="d-inline-flex align-items-center gap-2">
            <Icon size={16} className={iconClassName} />
            {name}
          </span>
        ),
        required: true,
        buttonClassName: 'd-flex align-items-center',
      },
    ],
    [formatMessage]
  );

  const handleBulkStatusSuccess = useCallback(
    (updatedViews: CalibrationViewSummary[]) => {
      onViewsUpdated(updatedViews);
      clearSelectedViews();
      toast.success(
        formatMessage(
          {
            id: 'app.views.widgets.dashboards.calibration_dashboard.list.status_modal.success',
            defaultMessage:
              'Status updated for {count, plural, one {# calibration view} other {# calibration views}}.',
          },
          { count: updatedViews.length }
        )
      );
    },
    [onViewsUpdated, clearSelectedViews, formatMessage]
  );

  const tableRows = useMemo<ViewListRow[]>(() => {
    const fixtureRows = calibrationViews.map((view) => ({
      id: view.id,
      name: view.name,
      status: view.status,
      dueDateLabel: view.due_date,
      ownerPerson: view.ownerPerson,
      administrators: view.administrators,
      administrator_ids: view.administrators.map((p) => p.id),
      reviewers: view.reviewers,
      reviewer_ids: view.reviewers.map((p) => p.id),
      reviewerCount: view.reviewers.length,
      revieweeDescription: isHRBP
        ? filterToText(view.config?.reviewees_filter)
        : '',
    }));

    return fixtureRows;
  }, [calibrationViews, filterToText, isHRBP]);

  const renderDeleteButton = useCallback(
    (row) => {
      // Mirrors the backend: owners and administrators may delete a draft view
      const isOwnerOrAdministrator =
        row.ownerPerson?.id === currentPerson?.id ||
        (currentPerson?.id !== undefined &&
          row.administrator_ids?.includes(currentPerson.id));
      const isDisabled = row.status !== 'D' || !isOwnerOrAdministrator;
      const targetId = `calibration-view-delete-${row.id}`;
      return (
        <>
          <span id={targetId}>
            <Button
              className="btn btn-light btn-sm"
              disabled={isDisabled}
              onClick={() => handleDeleteClick(row.id)}
            >
              <i
                className={`fe fe-trash-2 ${
                  isDisabled ? 'text-muted' : 'text-danger'
                }`}
              />
            </Button>
          </span>
          {isDisabled && (
            <UncontrolledPopover
              placement="top"
              trigger="hover"
              target={targetId}
            >
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.delete_disabled_popover_owner_or_administrator"
                defaultMessage="You can only delete this Calibration View if you're the owner or an administrator, and it's in Draft status."
              />
            </UncontrolledPopover>
          )}
        </>
      );
    },
    [handleDeleteClick, currentPerson]
  );

  const tableColumns = useMemo(() => {
    const statusHeader = buildIconHeader(
      formatMessage({
        id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.status',
        defaultMessage: 'Status',
      }),
      {
        iconNode: <PlayCircle size={14} />,
      }
    );

    return [
      {
        field: 'name',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.view',
          defaultMessage: 'Calibration view',
        }),
        hideFromFilters: true,
        includeCheckbox: true,
      },
      {
        field: 'dueDateLabel',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.due',
          defaultMessage: 'Due date',
        }),
        className: 'text-nowrap',
      },
      {
        field: 'status',
        name: statusHeader.content,
        suggestionConditionName: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.status',
          defaultMessage: 'Status',
        }),
        csvName: 'Status',
        popoverContent: statusHeader.popover,
        className: 'text-center',
        filterIcon: (_fieldName, fieldValue) =>
          CALIBRATION_STATUS_META(formatMessage)[fieldValue].iconName,
        getFilterDisplayValue: (status: CalibrationViewStatus) =>
          CALIBRATION_STATUS_META(formatMessage)[status].name,
        format: (
          status: CalibrationViewStatus,
          _uniqueIndexIdentifier: unknown,
          _rowIndex: unknown,
          row: ViewListRow
        ) => {
          const statusMeta = CALIBRATION_STATUS_META(formatMessage)[status];
          const statusLabelValue = statusMeta.name;
          const StatusIcon = statusMeta.Icon;
          const targetId = `calibration-view-status-${row.id}`;
          return (
            <>
              <span
                id={targetId}
                className={`d-inline-flex align-items-center justify-content-center ${statusMeta.iconClassName}`}
                aria-label={statusLabelValue}
              >
                <StatusIcon size={18} aria-hidden="true" />
              </span>
              <UncontrolledPopover
                placement="top"
                trigger="hover"
                target={targetId}
              >
                {statusLabelValue}
              </UncontrolledPopover>
            </>
          );
        },
        csvFormat: (status: CalibrationViewStatus) =>
          CALIBRATION_STATUS_META(formatMessage)[status].name,
      },
      {
        field: 'ownerPerson',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.owner',
          defaultMessage: 'Owner',
        }),
        isPerson: true,
        format: (person?: Person | null) =>
          person ? (
            <Avatar size="xs" person={person} />
          ) : (
            <span className="text-muted">
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.placeholder.owner"
                defaultMessage="No owner"
              />
            </span>
          ),
        csvName: ['Owner name', 'Owner email'],
        csvFormat: (person?: Person | null) => ({
          'Owner name': person?.full_name ?? '',
          'Owner email': person?.email ?? '',
        }),
        getFilterDisplayValue: (person?: Person | null) =>
          person?.full_name ?? '',
        sort: comparePeople,
      },
      {
        field: 'administrators',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.administrators',
          defaultMessage: 'Administrators',
        }),
        isPerson: true,
        sortable: false,
        nameTransformerFunction: (name) =>
          formatMessage(
            {
              id: 'app.views.widgets.dashboards.calibration_dashboard.list.person_as_administrator',
              defaultMessage: '{name} as administrator',
            },
            { name }
          ),
        getFilterDisplayValue: (p) => p.full_name,
        fieldTransformer: (value, operator) => {
          return `(${operator == 'EQ' ? 'in' : 'not-in'} "${
            value.filterObject.value
          }" administrator_ids)`;
        },
        csvName: ['Administrators', 'Administrator emails'],
        csvFormat: (admins: Person[]) => ({
          Administrators: admins.map((person) => person.full_name).join(', '),
          'Administrator emails': admins
            .map((person) => person.email)
            .join(', '),
        }),
        format: (admins: Person[]) =>
          admins.length ? (
            <AvatarGroup size="xs" people={admins} />
          ) : (
            <span className="text-muted">
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.placeholder.none"
                defaultMessage="None"
              />
            </span>
          ),
      },
      {
        field: 'reviewers',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.column.reviewers',
          defaultMessage: 'Reviewers',
        }),
        isPerson: true,
        sortable: false,
        nameTransformerFunction: (name) =>
          formatMessage(
            {
              id: 'app.views.widgets.dashboards.calibration_dashboard.list.person_as_reviewer',
              defaultMessage: '{name} as reviewer',
            },
            { name }
          ),
        getFilterDisplayValue: (p) => p.full_name,
        fieldTransformer: (value, operator) => {
          return `(${operator == 'EQ' ? 'in' : 'not-in'} "${
            value.filterObject.value
          }" reviewer_ids)`;
        },
        csvName: ['Reviewers', 'Reviewer emails'],
        csvFormat: (reviewers: Person[]) => ({
          Reviewers: reviewers.map((person) => person.full_name).join(', '),
          'Reviewer emails': reviewers.map((person) => person.email).join(', '),
        }),
        format: (reviewers: Person[]) =>
          reviewers.length ? (
            <AvatarGroup size="xs" people={reviewers} />
          ) : (
            <span className="text-muted">
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.placeholder.none"
                defaultMessage="None"
              />
            </span>
          ),
      },
      {
        field: 'id',
        name: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.actions_column',
          defaultMessage: 'Actions',
        }),
        hideFromCSV: true,
        hideFromFilters: true,
        className: 'text-end text-nowrap',
        sortable: false,
        format: (
          _value: string,
          _uniqueIndexIdentifier: any,
          _rowIndex: any,
          row: ViewListRow
        ) => (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => onSelectView(row.id)}
            >
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.open_button"
                defaultMessage="Open"
              />
            </Button>
            {renderDeleteButton(row)}
          </div>
        ),
      },
    ];
  }, [formatMessage, onSelectView, renderDeleteButton]);

  return (
    <>
      <ConfirmationDialogModal
        isOpen={deleteViewId !== null}
        onClosed={handleDeleteClose}
        toggle={handleDeleteClose}
        confirmCallback={handleDeleteConfirm}
        title={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.delete_modal.title',
          defaultMessage: 'Delete calibration view',
        })}
        description={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.delete_modal.description',
          defaultMessage:
            'Are you sure you want to delete this calibration view? This just deletes the view, any calibration and comments added to people will be preserved.',
        })}
        confirmText={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.delete_modal.confirm',
          defaultMessage: 'Delete',
        })}
        validationErrors={deleteErrorMessage}
      />
      <ModalEditor
        isOpen={isStatusModalOpen}
        toggle={toggleStatusModal}
        callback={handleBulkStatusSuccess}
        triggerCallbackBeforeClose={true}
        url="/calibration-views/bulk-update-status"
        method="POST"
        object={bulkStatusObject}
        inputs={bulkStatusInputs}
        title={formatMessage(
          {
            id: 'app.views.widgets.dashboards.calibration_dashboard.list.status_modal.title',
            defaultMessage:
              'Change status of {count, plural, one {# calibration view} other {# calibration views}}',
          },
          { count: selectedViewIds.size }
        )}
        submitText={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.list.status_modal.confirm',
          defaultMessage: 'Change status',
        })}
      />
      <div className="mb-3">
        <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-center justify-content-between">
          <h2 className="h4 mb-0">
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.list.title"
              defaultMessage="Calibration views"
            />
          </h2>
        </div>
        <p className="mt-2 mb-0 text-muted">
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.list.subtitle"
            defaultMessage="Preview the calibration views for this cycle. Choose one to explore calibrator progress and flagged employees."
          />
        </p>
      </div>
      <FilterablePeopleTable
        arrayValuesUsedForFormatting={false}
        rows={tableRows}
        columns={tableColumns}
        getUniqueRowId={(row: ViewListRow) => row.id}
        isRowSelectable={isRowSelectable}
        toggleSelectedRowField={toggleSelectedView}
        selectedRowToggleFieldsSet={selectedViewIds}
        setSelectedRowToggleFieldsSet={setSelectedViewIds}
        onAdvancedFiltersChange={clearSelectedViews}
        tableClassName="calibration-views-table"
        filterType={FilterType.ADVANCED}
        defaultSort={NAME_SORT}
        suggestionGenerator={suggestionGenerator}
        stickyHeader={true}
        actions={
          onCreateView || selectedViewIds.size > 0 ? (
            <Col className="col-auto ps-0 ms-auto d-flex flex-wrap gap-2 align-items-center">
              {selectedViewIds.size > 0 && (
                <Button color="light" size="sm" onClick={toggleStatusModal}>
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.list.action.change_status"
                    defaultMessage="Change status of {count, plural, one {# view} other {# views}}"
                    values={{ count: selectedViewIds.size }}
                  />
                </Button>
              )}
              {onCreateView && (
                <Button color="primary" size="sm" onClick={onCreateView}>
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.list.action.create"
                    defaultMessage="Create new calibration view"
                  />
                </Button>
              )}
            </Col>
          ) : undefined
        }
        exportActionTextFunction={(count) =>
          formatMessage(
            {
              id: 'app.views.widgets.dashboards.calibration_dashboard.list.export',
              defaultMessage: 'Export {count} calibration views to CSV',
            },
            { count: formatNumber(count) }
          )
        }
      />
    </>
  );
};

export default CalibrationViewList;
